import { fallbackEvents, fallbackHomePageContent, fallbackSiteSettings } from "@/content/fallback-content";
import { fetchCalendarEvents } from "@/services/calendar/events";
import type { ContentResult, EventData, HomePageContent, SiteSettings } from "@/services/sanity/types";

const projectId = import.meta.env.VITE_SANITY_PROJECT_ID || "bejzbcu2";
const dataset = import.meta.env.VITE_SANITY_DATASET || "production";
const apiVersion = "2023-05-03";
const isLocal = typeof window !== "undefined" && (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1");
const sanityOrigin = import.meta.env.DEV || isLocal ? "/api/sanity" : `https://${projectId}.apicdn.sanity.io`;

interface SanityResponse<T> {
    result?: T;
}

interface RawSiteSettings {
    address?: string;
    adminUrl?: string;
    contactEmail?: string;
    email?: string;
    phone?: string;
}

function formatSanityErrorMessage(error: unknown): string {
    const fallbackMessage = "Live content is temporarily unavailable. Showing saved content instead.";

    if (!(error instanceof Error)) {
        return fallbackMessage;
    }

    const statusMatch = error.message.match(/status (\d{3})/);
    const status = statusMatch ? Number(statusMatch[1]) : null;

    if (status === 404) {
        if (import.meta.env.DEV) {
            return `${fallbackMessage} Developer note: Sanity returned 404. Check VITE_SANITY_PROJECT_ID and VITE_SANITY_DATASET.`;
        }

        return fallbackMessage;
    }

    if (status === 403) {
        if (import.meta.env.DEV) {
            return `${fallbackMessage} Developer note: Sanity returned 403. Check dataset permissions or local proxy/CORS configuration.`;
        }

        return fallbackMessage;
    }

    if (import.meta.env.DEV) {
        return `${fallbackMessage} Developer note: ${error.message}.`;
    }

    return fallbackMessage;
}

function buildQueryUrl(query: string): string {
    const encodedQuery = encodeURIComponent(query);
    return `${sanityOrigin}/v${apiVersion}/data/query/${dataset}?query=${encodedQuery}`;
}

async function fetchQuery<T>(query: string): Promise<T> {
    const response = await fetch(buildQueryUrl(query));

    if (!response.ok) {
        throw new Error(`Sanity request failed with status ${response.status}`);
    }

    const payload = (await response.json()) as SanityResponse<T>;
    return payload.result as T;
}

function mergeSiteSettings(settings?: Partial<SiteSettings> | null): SiteSettings {
    return {
        ...fallbackSiteSettings,
        ...settings,
    };
}

function mapSiteSettings(settings?: RawSiteSettings | null): Partial<SiteSettings> | null {
    if (!settings) {
        return null;
    }

    return {
        address: settings.address,
        adminUrl: settings.adminUrl,
        contactEmail: settings.contactEmail || settings.email,
        phone: settings.phone,
    };
}

function mergeHomePageContent(content?: Partial<HomePageContent> | null): HomePageContent {
    const homepage = content?.homepage;
    const whatToExpect = homepage?.whatToExpect;

    return {
        homepage: {
            ...fallbackHomePageContent.homepage,
            ...homepage,
            whatToExpect: {
                ...fallbackHomePageContent.homepage.whatToExpect,
                ...whatToExpect,
                cards: whatToExpect?.cards?.length ? whatToExpect.cards : fallbackHomePageContent.homepage.whatToExpect.cards,
            },
        },
        series: content?.series
            ? {
                  ...fallbackHomePageContent.series,
                  ...content.series,
              }
            : fallbackHomePageContent.series,
        events: Array.isArray(content?.events) ? content.events : fallbackHomePageContent.events,
    };
}

export async function getSiteSettings(): Promise<ContentResult<SiteSettings>> {
    try {
        const settings = await fetchQuery<RawSiteSettings | null>('*[_type == "settings"][0]{address, email, phone, contactEmail, adminUrl}');
        return {
            data: mergeSiteSettings(mapSiteSettings(settings)),
            source: "sanity",
        };
    } catch (error) {
        console.warn("Falling back to local site settings content.", error);
        return {
            data: fallbackSiteSettings,
            source: "fallback",
            error: formatSanityErrorMessage(error),
        };
    }
}

export async function getHomePageContent(): Promise<ContentResult<HomePageContent>> {
    // 1. Try to fetch live upcoming events from Google Calendar (fetch up to 20 so highlighted events further out can be pinned)
    let calendarEvents: EventData[] = [];
    try {
        const calResult = await fetchCalendarEvents(20);
        if (calResult.source === "calendar" && calResult.data.length > 0) {
            calendarEvents = calResult.data;
        }
    } catch {
        // Continue to Sanity query
    }

    try {
        const content = await fetchQuery<Partial<HomePageContent>>(`{
      "homepage": *[_type == "homepage"][0],
      "series": *[_type == "series" && isActive == true][0],
      "events": *[_type == "event" && isUpcoming != false && dateTime(coalesce(endDate, date)) >= dateTime(now()) - 86400] | order(date asc)[0..5] {
        title, description, date, endDate, "image": image.asset->url, location, address, googleMapsLink, isUpcoming
      }
    }`);

        const merged = mergeHomePageContent(content);
        if (calendarEvents.length > 0) {
            merged.events = calendarEvents;
        }

        return {
            data: merged,
            source: calendarEvents.length > 0 ? "calendar" : "sanity",
        };
    } catch (error) {
        console.warn("Falling back to local homepage content.", error);
        const fallback = { ...fallbackHomePageContent };
        if (calendarEvents.length > 0) {
            fallback.events = calendarEvents;
        }
        return {
            data: fallback,
            source: calendarEvents.length > 0 ? "calendar" : "fallback",
            error: formatSanityErrorMessage(error),
        };
    }
}

export async function getEvents(): Promise<ContentResult<EventData[]>> {
    // 1. Try to fetch live events from Google Calendar first (including recent past events)
    try {
        const calResult = await fetchCalendarEvents(30, true);
        if (calResult.source === "calendar" && calResult.data.length > 0) {
            return calResult;
        }
    } catch {
        // Fall back to Sanity
    }

    // 2. Fall back to Sanity Studio events
    try {
        const events = await fetchQuery<EventData[]>(
            '*[_type == "event"] | order(date asc) { title, description, date, endDate, "image": image.asset->url, location, address, googleMapsLink, isUpcoming }',
        );
        return {
            data: events,
            source: "sanity",
        };
    } catch (error) {
        console.warn("Falling back to local events content.", error);
        return {
            data: fallbackEvents,
            source: "fallback",
            error: formatSanityErrorMessage(error),
        };
    }
}

export { mergeHomePageContent, mergeSiteSettings };
export { isEventPast, isEventUpcoming, filterUpcomingEvents, filterPastEvents, selectHomeUpcomingEvents } from "./events";
export type { EventData, HomepageData, SeriesData, SiteSettings } from "@/services/sanity/types";
